import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { API_URL } from "../config/appConfig";
import {
  AUTH_SESSION_CHANGED_EVENT,
  clearAuthSession,
  getAuthToken,
  getStoredUser,
  saveAuthSession,
} from "./authStorage";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(getAuthToken());
  const [user, setUser] = useState(getStoredUser());

  useEffect(() => {
    const syncAuthState = () => {
      setToken(getAuthToken());
      setUser(getStoredUser());
    };

    const handleStorageChange = (event) => {
      if (!event.key || event.key === "auth_token" || event.key === "auth_user") {
        syncAuthState();
      }
    };

    window.addEventListener(AUTH_SESSION_CHANGED_EVENT, syncAuthState);
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener(AUTH_SESSION_CHANGED_EVENT, syncAuthState);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const login = async (userName, password) => {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ userName, password }),
    });

    if (!response.ok) {
      throw new Error("Login failed");
    }

    const data = await response.json();
    const normalizedUser = {
      userName: data.userName,
      displayName: data.displayName,
    };

    saveAuthSession(data.token, normalizedUser);
    setToken(data.token);
    setUser(normalizedUser);
  };

  const logout = () => {
    clearAuthSession();
    setToken("");
    setUser(null);
  };

  const value = useMemo(() => {
    return {
      token,
      user,
      isAuthenticated: Boolean(token),
      login,
      logout,
    };
  }, [token, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }

  return context;
};
